import ResourceTyped from 'Resources/ResourceTyped';

type Conversation = {
	id: number;
	title: string;
	starred: boolean;
	contextType: string;
	contextId?: number;
	regDate: string;
	modDate: string;
	messages?: AssistantMessage[];
};

type AssistantMessage = {
	id: number;
	role: 'user' | 'assistant' | 'tool';
	content: string;
	metadata?: Record<string, unknown>;
	regDate: string;
};

type Memory = {
	id: number;
	type: string;
	key: string;
	value: string;
	source: string;
	regDate: string;
	modDate: string;
};

type Preference = {
	id: number;
	capability: string;
	enabled: boolean;
	settings?: Record<string, unknown>;
};

export type RoutineScope = 'user' | 'org';

export type HookFilter = {
	field: string;
	operator: string;
	value?: string | number | boolean | null;
};

export type Routine = {
	id: number;
	userId: number;
	scope: RoutineScope;
	agentId?: number | null;
	label?: string;
	prompt?: string;
	/** When set, the routine fires the workflow runner instead of the agent prompt path. */
	workflowId?: number | null;
	/** Optional trigger-payload override for workflow runs (cron-scoped only; event-scoped routines get the entity payload). */
	payload?: Record<string, unknown> | null;
	enabled: boolean;
	conversationId?: number;
	cronExpression?: string | null;
	lastRun?: string;
	nextRun?: string;
	entityType?: string | null;
	event?: 'insert' | 'update' | null;
	filters?: HookFilter[];
	cooldownSeconds?: number;
	lastFired?: string;
	regDate?: string;
	modDate?: string;
};

/**
 * Mirror of the backend `CompiledWorkflowDraft` shape (see
 * `base/src/core/services/ai/agent/workflow/compiler/llmCompiler.types.ts`)
 * with only the fields the read-only review modal needs. Backend types use
 * `unknown` for `args` and similar — we keep that loose here so the modal
 * can render whatever the compiler produced without lying about shape.
 */
export type CompiledPlaybookStep =
	| { kind: 'tool'; id: string; tool: string; args: unknown; bind?: string; onError?: string }
	| { kind: 'llm'; id: string; prompt: unknown; output: unknown; bind: string; onError?: string }
	| { kind: 'if'; id: string; cond: string; then: string[]; else?: string[] }
	| { kind: 'foreach'; id: string; over: string; as: string; body: string[]; bind?: string };

export type CompiledPlaybookArgDecision =
	| { kind: 'keep-constant'; rationale: string }
	| { kind: 'keep-binding'; rationale: string }
	| { kind: 'promote-template'; template: string; rationale: string }
	| { kind: 'promote-llm-step'; promotedStepId: string; rationale: string };

export type CompiledPlaybookWarning = {
	code: string;
	stepId?: string;
	argPath?: string;
	path?: string;
	message: string;
};

/**
 * WEB-6799 — single inbox item. v1 only emits `kind: 'approval'`; future
 * iterations may add delegations + scout findings as new kinds. Consumers
 * should switch on `kind` rather than assume approval-shape fields exist.
 */
export type InboxItem = {
	id: number;
	kind: 'approval';
	action: string;
	conversationId: number;
	runId: number;
	agentId: number;
	regDate: string;
	expiresAt?: string;
};

export type CompiledPlaybookDraft = {
	sourceRunId: number;
	workflow: {
		trigger: { kind: string; [k: string]: unknown };
		steps: CompiledPlaybookStep[];
	};
	provenance: Record<
		string,
		{
			sourceEventSeq: number;
			args: Record<string, { kind: 'constant' | 'binding-step' | 'binding-trigger' | 'generated'; [k: string]: unknown }>;
		}
	>;
	warnings: CompiledPlaybookWarning[];
	validationIssues: Array<{ message: string; path?: string }>;
	decisions: Record<string, CompiledPlaybookArgDecision>;
	compilerWarnings: CompiledPlaybookWarning[];
	compilerVersion: { model: string; promptHash: string };
};

type CustomerKnowledge = {
	id: number;
	category: string;
	key: string;
	value: string;
	confidence: number;
	usageCount: number;
	lastValidated?: string;
};

export type AdminUsage = {
	period: { label: string; from: string; to: string };
	orgBudget: {
		spentCents: number;
		spentCreditsSek: number;
		spentSek: number;
		limitSek: number;
		dailySeries: number[];
		dailySeriesCreditsSek: number[];
	};
	users: Array<{
		userId: number;
		name: string;
		role: string | null;
		personalSpendCents: number;
		personalSpendCreditsSek: number;
		personalSpendSek: number;
		orgSpendCents: number;
		orgSpendCreditsSek: number;
		orgSpendSek: number;
		agentCount: number;
		canCreateAgents: boolean;
		canAccessWorkspace: boolean;
		isAdmin: boolean;
		/** WEB-6650 — explicit per-user override; null = inherit default. */
		aiCreditCapOverrideSek: number | null;
		/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		ownerUserId: number | null;
		ownerName: string | null;
		avatarUrl: string | null;
		tasks: number;
		successPct: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	promotionPending: number;
	kpis: {
		totalSpendCents: number;
		totalSpendCreditsSek: number;
		tasks: number;
		avgSuccessPct: number;
		topSpenderUserId: number | null;
		mostUsedAgentId: number | null;
	};
};

export type CreditsSnapshot = {
	period: { label: string; from: string; to: string };
	allocatedSek: number;
	consumedSek: number;
	remainingSek: number;
	overageSek: number;
	activeUserCount: number;
	effectiveUsdToSekRate: number;
	marginMultiplier: number;
	perUser: Array<{
		userId: number;
		name: string | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
		/** WEB-6650 — resolved per-user credit cap (SEK). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	perAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
	}>;
	perTool: Array<{ feature: string; costUsd: number; creditsSek: number; callCount: number }>;
	perDay: Array<{ day: string; costUsd: number; creditsSek: number; callCount: number }>;
	customerPaidConnections: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
	customerPaidPerUser: Array<{ userId: number; name: string | null; callCount: number }>;
	customerPaidPerAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		callCount: number;
	}>;
	customerPaidPerDay: Array<{ day: string; callCount: number }>;
	// WEB-6651: notetaker (and any future tool) usage covered by a legacy
	// addon. Same shape as customerPaidConnections; differentiated server-side
	// by `metadata.coverage === 'addon'` on the ai_tracking row. Never folded
	// into credits — the addon already pays for it.
	addonCoveredUsage: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
};

export type PriceBookItem = {
	toolKey: string;
	vendor: string;
	category: 'llm' | 'web' | 'notetaker' | 'other';
	unit: string;
	pricePerUnitSek: number;
};

export type PromotionRequest = {
	id: number;
	agentId: number;
	requestedByUserId: number;
	pitchText: string | null;
	status: 'pending' | 'approved' | 'declined';
	decidedByUserId?: number | null;
	decidedAt?: string | null;
	regDate: string;
	modDate: string;
};

export type UserUsage = {
	personalSpendCents: number;
	personalSpendCreditsSek: number;
	personalSpendSek: number;
	personalCapSek: number;
	/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
	effectivePersonalCreditCapSek: number;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		status: string;
		avatarUrl: string | null;
		budgetMonthlySek: number;
		spentMonthlyCents: number;
		spentMonthlySek: number;
		spentMonthlyCreditsSek: number;
		tasksCompleted14d: number;
		tasksFailed14d: number;
		pausedReason: string | null;
	}>;
	totalSpendCents: number;
	totalSpendCreditsSek: number;
	tasksCompleted14d: number;
	tasksFailed14d: number;
	successRate14d: number;
	histogram: Array<{
		day: string;
		completed: number;
		failed: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	pool: {
		period: { label: string; from: string; to: string };
		allocatedSek: number;
		consumedSek: number;
		remainingSek: number;
		overageSek: number;
		userCreditsSek: number;
		userSharePct: number;
	} | null;
};

export type AiAgentPolicies = {
	/**
	 * Org-wide default per-user monthly cap on personal AI agent spend (SEK).
	 * Also the WEB-6650 fallback when a user has no `ai_credit_cap_sek`
	 * override. 0 = unlimited.
	 */
	personalCapSek: number;
	orgMonthlyBudgetSek: number;
	promotionMinTasks: number;
	promotionMinSuccessPct: number;
	minExecutionIntervalMinutes: number;
	permissionUpgradeMinApprovals: number;
};

class Assistant extends ResourceTyped<Conversation> {
	constructor() {
		super('assistant/conversations');
	}

	createConversation(contextType = 'general', contextId?: number): Promise<{ id: number }> {
		return this._postRequest('', { contextType, contextId }).then(res => res.data?.data ?? res.data);
	}

	getConversation(id: number): Promise<Conversation> {
		return this._getRequest(String(id)).then(res => res.data?.data ?? res.data);
	}

	deleteConversation(id: number): Promise<void> {
		return this._deleteRequest(String(id)).then(() => undefined);
	}

	renameConversation(id: number, title: string): Promise<void> {
		return this._putRequest(String(id), { title }).then(() => undefined);
	}

	starConversation(id: number, starred: boolean): Promise<void> {
		return fetch(`/api/v2/assistant/conversations/${id}/star`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ starred })
		}).then(() => undefined);
	}

	updatePostMeetingMessage(
		conversationId: number,
		messageId: number,
		updates: { content?: string; title?: string }
	): Promise<void> {
		return this._putRequest(`${conversationId}/post-meeting-message/${messageId}`, updates).then(() => undefined);
	}

	listConversations(): Promise<Conversation[]> {
		return this._getRequest('').then(res => res.data?.data ?? res.data);
	}

	async sendMessageStream(
		conversationId: number,
		message: string,
		contextType: string = 'general',
		contextId?: number,
		options: {
			files?: File[];
			isScheduledRun?: boolean;
			runId?: string;
			runTimestamp?: string;
			regenerate?: boolean;
			signal?: AbortSignal;
			onText?: (text: string) => void;
			onDone?: () => void;
			onError?: (error: string) => void;
			onPanel?: (panel: unknown) => void;
			onImages?: (urls: string[]) => void;
			onAttachments?: (
				attachments: Array<{ name: string; mimeType: string; sizeBytes?: number; url?: string }>
			) => void;
			onStatus?: (status: string) => void;
			onMultiAction?: (config: unknown) => void;
			onWriteProposal?: (proposal: {
				entity: string;
				action: string;
				data: Record<string, unknown>;
				approvalId?: number | null;
			}) => void;
			onEmailDraft?: (draft: {
				contactIds: number[];
				subject: string;
				body: string;
				ccContactIds?: number[];
				clientId?: number;
				approvalId?: number | null;
			}) => void;
			onAgentCreated?: (info: { agentId: number; name: string; updated?: boolean }) => void;
			onIdentityUpdated?: (identity: { name?: string; role?: string; avatarUrl?: string | null }) => void;
			onThinking?: (snapshot: string) => void;
			onSuggestedReplies?: (replies: string[]) => void;
		} = {}
	): Promise<void> {
		const {
			files,
			isScheduledRun,
			runId,
			runTimestamp,
			regenerate,
			signal,
			onText,
			onDone,
			onError,
			onPanel,
			onImages,
			onAttachments,
			onStatus,
			onMultiAction,
			onWriteProposal,
			onEmailDraft,
			onAgentCreated,
			onIdentityUpdated,
			onThinking,
			onSuggestedReplies
		} = options;
		let body: BodyInit;
		const headers: Record<string, string> = {};

		if (files && files.length > 0) {
			const formData = new FormData();
			formData.append('message', message);
			formData.append('contextType', contextType);
			if (contextId !== undefined) formData.append('contextId', String(contextId));
			for (const file of files) {
				formData.append('files', file);
			}
			body = formData;
		} else {
			headers['Content-Type'] = 'application/json';
			body = JSON.stringify({
				message,
				contextType,
				contextId,
				...(isScheduledRun && { isScheduledRun: true }),
				...(runId && { runId }),
				...(runTimestamp && { runTimestamp }),
				...(regenerate && { regenerate: true })
			});
		}

		let response: Response;
		try {
			response = await fetch(`/api/v2/assistant/conversations/${conversationId}/messages`, {
				method: 'POST',
				headers,
				body,
				signal
			});
		} catch (err) {
			// Silent exit on user-triggered abort — the caller is responsible for
			// cleaning up UI state. Anything else surfaces as an error.
			if (err instanceof DOMException && err.name === 'AbortError') return;
			onError?.('Failed to send message');
			return;
		}

		if (!response.ok || !response.body) {
			onError?.('Failed to send message');
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		let gotDone = false;
		let gotError = false;
		let streamDone = false;

		while (!streamDone) {
			let readResult: ReadableStreamReadResult<Uint8Array>;
			try {
				readResult = await reader.read();
			} catch (err) {
				// Abort during read — stop cleanly without firing onError.
				if (err instanceof DOMException && err.name === 'AbortError') return;
				throw err;
			}
			const { done, value } = readResult;
			if (done) {
				streamDone = true;
				break;
			}

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';

			for (const line of lines) {
				if (line.startsWith('data: ')) {
					try {
						const data = JSON.parse(line.slice(6));
						if (data.type === 'text' && data.text) {
							onText?.(data.text);
						} else if (data.type === 'panel' && data.panel) {
							onPanel?.(data.panel);
						} else if (data.type === 'images' && data.urls) {
							onImages?.(data.urls);
						} else if (data.type === 'attachments' && Array.isArray(data.attachments)) {
							onAttachments?.(data.attachments);
						} else if (data.type === 'status' && data.text) {
							onStatus?.(data.text);
						} else if (data.type === 'multi_action' && data.config) {
							onMultiAction?.(data.config);
						} else if (data.type === 'write_proposal' && data.proposal) {
							onWriteProposal?.(data.proposal);
						} else if (data.type === 'email_draft' && data.emailDraft) {
							onEmailDraft?.(data.emailDraft);
						} else if (data.type === 'agent_created' && data.agentCreated) {
							onAgentCreated?.(data.agentCreated);
						} else if (data.type === 'identity_updated' && data.identity) {
							onIdentityUpdated?.(data.identity);
						} else if (data.type === 'thinking' && data.text) {
							onThinking?.(data.text);
						} else if (data.type === 'suggested_replies' && Array.isArray(data.suggestedReplies)) {
							onSuggestedReplies?.(
								data.suggestedReplies.filter((r: unknown): r is string => typeof r === 'string')
							);
						} else if (data.type === 'done') {
							gotDone = true;
							onDone?.();
						} else if (data.type === 'error') {
							gotError = true;
							onError?.(data.error);
						}
					} catch {
						// Skip malformed events
					}
				}
			}
		}

		// Process any remaining data in the buffer
		if (buffer.trim().startsWith('data: ')) {
			try {
				const data = JSON.parse(buffer.trim().slice(6));
				if (data.type === 'text' && data.text) {
					onText?.(data.text);
				} else if (data.type === 'done') {
					gotDone = true;
					onDone?.();
				}
			} catch {
				// Skip malformed
			}
		}

		// If stream ended without a done event, call onDone anyway (unless error already handled it)
		if (!gotDone && !gotError) {
			onDone?.();
		}
	}

	getMemories(): Promise<Memory[]> {
		return fetch('/api/v2/assistant/memory')
			.then(res => res.json())
			.then(res => res.data);
	}

	updateMemory(memoryId: number, value: string): Promise<void> {
		return fetch(`/api/v2/assistant/memory/${memoryId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ value })
		}).then(() => undefined);
	}

	deleteMemory(memoryId: number): Promise<void> {
		return fetch(`/api/v2/assistant/memory/${memoryId}`, { method: 'DELETE' }).then(() => undefined);
	}

	getCustomerKnowledge(): Promise<CustomerKnowledge[]> {
		return fetch('/api/v2/assistant/customer-knowledge')
			.then(res => res.json())
			.then(res => res.data);
	}

	createCustomerKnowledge(entry: {
		category: string;
		key: string;
		value: string;
		confidence?: number;
	}): Promise<CustomerKnowledge> {
		return fetch('/api/v2/assistant/customer-knowledge', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(entry)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	updateCustomerKnowledge(
		id: number,
		updates: { key?: string; value?: string; category?: string; confidence?: number }
	): Promise<void> {
		return fetch(`/api/v2/assistant/customer-knowledge/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(() => undefined);
	}

	deleteCustomerKnowledge(id: number): Promise<void> {
		return fetch(`/api/v2/assistant/customer-knowledge/${id}`, { method: 'DELETE' }).then(() => undefined);
	}

	getPreferences(): Promise<Preference[]> {
		return fetch('/api/v2/assistant/preferences')
			.then(res => res.json())
			.then(res => res.data);
	}

	updatePreference(capability: string, enabled: boolean, settings?: Record<string, unknown>): Promise<void> {
		return fetch('/api/v2/assistant/preferences', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ capability, enabled, settings })
		}).then(() => undefined);
	}

	getRoutines(): Promise<Routine[]> {
		return fetch('/api/v2/assistant/routines')
			.then(res => res.json())
			.then(res => res.data);
	}

	/**
	 * WEB-6799 — fetch the user's Inbox. v1 = pending approval requests
	 * targeted at the caller, oldest first. Backend filters; client just
	 * renders. Caller is expected to gate on `useInboxAccess()` before
	 * mounting the consumer.
	 */
	getInbox(): Promise<InboxItem[]> {
		return fetch('/api/v2/assistant/inbox')
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	/**
	 * Compile a Playbook draft from the routine's most recent succeeded run.
	 * Returns the raw `CompiledPlaybookDraft` for read-only review (no DB write
	 * — the visual editor is what persists). Throws with a user-readable
	 * message when the routine has no succeeded run yet.
	 */
	async compilePlaybookFromRoutine(routineId: number): Promise<CompiledPlaybookDraft> {
		const res = await fetch('/api/v2/agentWorkflows/compile-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Compile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as CompiledPlaybookDraft;
	}

	/**
	 * Compile a Playbook draft from a specific `agent_run` (WEB-6857). Used by
	 * the reply-attached "Compile to Playbook" button — the run id is read
	 * from `assistant_message.metadata.agentRunId`. No DB write; returns the
	 * draft for the review modal.
	 */
	async compilePlaybookFromRun(runId: number): Promise<CompiledPlaybookDraft> {
		const res = await fetch('/api/v2/agentWorkflows/compile-from-run', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ runId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Compile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as CompiledPlaybookDraft;
	}

	/**
	 * Persist a compiled Playbook draft (WEB-6850 / WEB-6857).
	 *
	 * Two modes:
	 * - **Routine-bound** (`routineId: number`) — also binds the routine's
	 *   `workflowId` so its next trigger dispatches the workflow runner
	 *   instead of the agent prompt path.
	 * - **Standalone** (`routineId: null`) — just persists the `agent_workflow`
	 *   row; the user wires a trigger later. Permission falls back to source
	 *   run ownership on the backend.
	 */
	async savePlaybookFromCompiled(
		routineId: number | null,
		draft: CompiledPlaybookDraft
	): Promise<{ workflowId: number }> {
		const res = await fetch('/api/v2/agentWorkflows/save-from-compiled', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(routineId == null ? { draft } : { routineId, draft })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Save request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as { workflowId: number };
	}

	/**
	 * Unbind the routine's Playbook so it reverts to the agent-prompt path on
	 * the next trigger. The previous `agent_workflow` row stays in the DB as
	 * an audit trail — only `routine.workflowId` is cleared.
	 */
	async unsetPlaybookFromRoutine(routineId: number): Promise<void> {
		const res = await fetch('/api/v2/agentWorkflows/unset-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Unset request failed (${res.status})`;
			throw new Error(message);
		}
	}

	/**
	 * Recompile the routine's Playbook from its latest succeeded run. Creates
	 * a fresh `agent_workflow` row and rebinds the routine to it; the previous
	 * workflow row stays in the DB for audit. Returns the new workflow id and
	 * the just-saved draft so the UI can show it in a read-only review modal
	 * without a second round-trip.
	 */
	async recompilePlaybookFromRoutine(
		routineId: number
	): Promise<{ workflowId: number; draft: CompiledPlaybookDraft }> {
		const res = await fetch('/api/v2/agentWorkflows/recompile-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Recompile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as { workflowId: number; draft: CompiledPlaybookDraft };
	}

	createRoutine(routine: {
		scope?: RoutineScope;
		label?: string;
		prompt?: string;
		cronExpression?: string;
		entityType?: string;
		event?: 'insert' | 'update';
		filters?: HookFilter[];
		cooldownSeconds?: number;
		agentId?: number | null;
	}): Promise<Routine> {
		return fetch('/api/v2/assistant/routines', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(routine)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	async updateRoutine(
		routineId: number,
		updates: {
			scope?: RoutineScope;
			label?: string;
			prompt?: string;
			cronExpression?: string | null;
			entityType?: string | null;
			event?: 'insert' | 'update' | null;
			filters?: HookFilter[] | null;
			cooldownSeconds?: number;
			enabled?: boolean;
			agentId?: number | null;
		}
	): Promise<void> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.msg || `Failed to update routine (${res.status})`);
		}
	}

	deleteRoutine(routineId: number): Promise<void> {
		return fetch(`/api/v2/assistant/routines/${routineId}`, { method: 'DELETE' }).then(() => undefined);
	}

	getProfile(): Promise<{ name: string; title?: string; avatarUrl: string | null; conversationCount: number }> {
		return fetch('/api/v2/assistant/profile')
			.then(res => res.json())
			.then(res => res.data);
	}

	updateProfile(updates: { name?: string; title?: string; avatarUrl?: string | null }): Promise<void> {
		return fetch('/api/v2/assistant/profile', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(() => undefined);
	}

	generateAvatar(): Promise<{ avatarUrl: string }> {
		return fetch('/api/v2/assistant/avatar/generate', { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	submitFeedback(params: {
		type: 'up' | 'down';
		reason: string;
		assistantMessage?: string;
		userMessage?: string;
		conversationId?: number;
	}): Promise<{ saved: boolean; savedValue?: string; ignored: boolean; ignoreReason?: string }> {
		return fetch('/api/v2/assistant/feedback', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeWrite(entity: string, action: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/write', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ entity, action, data })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeSendEmail(
		contactIds: number[],
		subject: string,
		body: string,
		ccContactIds?: number[],
		clientId?: number
	): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/send-email', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ contactIds, subject, body, ccContactIds, clientId })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	reset(): Promise<void> {
		return fetch('/api/v2/assistant/reset', { method: 'POST' }).then(() => undefined);
	}

	getUserUsage(): Promise<UserUsage> {
		return fetch('/api/v2/assistant/usage')
			.then(res => res.json())
			.then(res => res.data);
	}

	getAdminUsage(period?: string): Promise<AdminUsage> {
		const qs = period ? `?period=${encodeURIComponent(period)}` : '';
		return fetch(`/api/v2/assistant/admin/usage${qs}`)
			.then(res => res.json())
			.then(res => res.data);
	}

	getAccountCredits(
		period?: string,
		filters?: { userIds?: number[]; agentIds?: number[]; features?: string[] }
	): Promise<CreditsSnapshot | null> {
		const params = new URLSearchParams();
		if (period) params.set('period', period);
		if (filters?.userIds?.length) params.set('users', filters.userIds.join(','));
		if (filters?.agentIds?.length) params.set('agents', filters.agentIds.join(','));
		if (filters?.features?.length) params.set('features', filters.features.join(','));
		const qs = params.toString();
		return fetch(`/api/v2/assistant/admin/credits${qs ? `?${qs}` : ''}`)
			.then(res => res.json())
			.then(res => res.data ?? null);
	}

	getToolPricing(): Promise<PriceBookItem[]> {
		return fetch('/api/v2/assistant/admin/tool-pricing')
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	listPromotionRequests(status: 'pending' | 'approved' | 'declined' = 'pending'): Promise<PromotionRequest[]> {
		return fetch(`/api/v2/assistant/admin/promotion-requests?status=${status}`)
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	requestPromotion(agentId: number, pitchText: string): Promise<PromotionRequest> {
		return fetch('/api/v2/assistant/promotion-requests', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ agentId, pitchText })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	approvePromotionRequest(id: number): Promise<{ request: PromotionRequest; newAgentId: number }> {
		return fetch(`/api/v2/assistant/admin/promotion-requests/${id}/approve`, { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	declinePromotionRequest(id: number): Promise<PromotionRequest> {
		return fetch(`/api/v2/assistant/admin/promotion-requests/${id}/decline`, { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	getAiAgentPolicies(): Promise<AiAgentPolicies> {
		return fetch('/api/v2/assistant/admin/policies')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAiAgentPolicies(updates: Partial<AiAgentPolicies>): Promise<AiAgentPolicies> {
		return fetch('/api/v2/assistant/admin/policies', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getAgentCreatorUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAgentCreatorUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveAgentCreatorUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	getWorkspaceUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveWorkspaceUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveWorkspaceUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	/**
	 * WEB-6650 — set or clear a single user's AI credit cap override.
	 * Pass `null` for `sek` to remove the override (user falls back to the
	 * org-wide `aiAgent.personalCapSek`).
	 */
	saveUserCreditCap(userId: number, sek: number | null): Promise<{ userId: number; sek: number | null }> {
		return fetch(`/api/v2/assistant/admin/users/${userId}/credit-cap`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sek })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getMyPermissions(): Promise<{ canCreateAgents: boolean; canAccessWorkspace: boolean }> {
		return fetch('/api/v2/assistant/me/permissions')
			.then(res => res.json())
			.then(res => res.data);
	}
}

const resource = new Assistant();

export default resource;
